import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Button, Typography } from '@mui/material';
import { PassengerIngestionServerConfiguration } from '../../../../data/models/passenger-ingestion-server-configuration.model';
import { useCommonTranslation } from '../../../../hooks/useCommonTranslation';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import * as clientSlice from '../../../../data/client.slice';
import * as lookupSlice from '../../../../data/lookup.slice';
import ClientServerConfigList from './ClientServerConfigList';
import ActivateDialog from '../../ActivateDialog';
import ConfirmationDialog from '../../../ConfirmationDialog';
import PassengerIngestionServerConfigDialog from './PassengerIngestionServerConfigDialog';
import { useIsSuperReaderUser } from '../../../../hooks/permissions/useIsSuperReaderUser';

const PREFIX = 'ClientServerConfigs';

const classes = {
  container: `${PREFIX}-container`,
  header: `${PREFIX}-header`,
  title: `${PREFIX}-title`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  [`& .${classes.header}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(3),
  },
  [`& .${classes.title}`]: {
    fontFamily: 'Bitter',
    fontWeight: 700,
    fontSize: 30,
  },
}));

interface ClientServerConfigsProps {
  clientBC: string;
}

export default function ClientServerConfigs({
  clientBC,
}: ClientServerConfigsProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();

  const serverConfigs = useAppSelector(
    clientSlice.selectPassengerIngestionServerConfigs,
  );
  const isSuperReaderUser = useIsSuperReaderUser();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [editingServerConfig, setEditingServerConfig] =
    useState<PassengerIngestionServerConfiguration>();
  const [deletedServerConfig, setDeletedServerConfig] =
    useState<PassengerIngestionServerConfiguration>();
  const [isDeactivate, setIsDeactivate] = useState(false);
  const [activateDialogTitle, setActivateDialogTitle] = useState('');
  const [activateDialogMessage, setActivateDialogMessage] = useState('');
  const [activateDialogPostiveFn, setActivateDialogPostiveFn] = useState<
    () => void
  >(() => null);
  const [activateDialogNegativeFn, setActivateDialogNegativeFn] = useState<
    () => void
  >(() => null);
  const [activateDialogOpen, setActivateDialogOpen] = useState(false);

  useEffect(() => {
    dispatch(lookupSlice.fetchLookupItemsAsync('ingestionCronJob'));
  }, []);
  useEffect(() => {
    if (clientBC) {
      dispatch(clientSlice.fetchPassengerIngestionServerConfigsAsync(clientBC));
    }
  }, [clientBC]);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingServerConfig(undefined);
  };

  const onCreate = () => {
    setEditingServerConfig(undefined);
    setDialogOpen(true);
  };

  const onEdit = (
    evt: any,
    serverConfig: PassengerIngestionServerConfiguration,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();
    setEditingServerConfig(serverConfig);
    setDialogOpen(true);
  };

  const activateServerConfig = (
    evt: any,
    serverConfig: PassengerIngestionServerConfiguration,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();

    const confirm = (start: string, end: string) => {
      dispatch(
        clientSlice.updateServerConfigEffectiveDatesAsync({
          clientBC: serverConfig.clientBC,
          passengerEligibilitySettingUUID:
            serverConfig.passengerEligibilitySettingUUID,
          effectiveStartDate: start,
          effectiveEndDate: end,
        } as any),
      );
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(false);
    setActivateDialogTitle(t('client.confirmActivateServerConfigTitle'));
    setActivateDialogMessage(t('client.confirmActivateServerConfigMessage'));
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const deactivateServerConfig = (
    evt: any,
    serverConfig: PassengerIngestionServerConfiguration,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();

    const confirm = (start?: string, end?: string) => {
      dispatch(
        clientSlice.updateServerConfigEffectiveDatesAsync({
          clientBC: serverConfig.clientBC,
          passengerEligibilitySettingUUID:
            serverConfig.passengerEligibilitySettingUUID,
          effectiveStartDate: start,
          effectiveEndDate: end,
        } as any),
      );
      setActivateDialogOpen(false);
    };
    const cancel = () => {
      setActivateDialogOpen(false);
    };
    setIsDeactivate(true);
    setActivateDialogTitle(t('client.confirmDeactivateServerConfigTitle'));
    setActivateDialogMessage(t('client.confirmDeactivateServerConfigMessage'));
    setActivateDialogPostiveFn(() => confirm);
    setActivateDialogNegativeFn(() => cancel);
    setActivateDialogOpen(true);
  };

  const onDelete = (
    evt: any,
    serverConfig: PassengerIngestionServerConfiguration,
  ) => {
    evt.preventDefault();
    evt.stopPropagation();
    setDeletedServerConfig(serverConfig);
    setConfirmDialogOpen(true);
  };

  const confirmDelete = () => {
    if (deletedServerConfig) {
      dispatch(
        clientSlice.deleteServerConfigAsync(
          clientBC,
          deletedServerConfig.passengerEligibilitySettingUUID,
        ),
      );
    }
    setConfirmDialogOpen(false);
  };

  const cancelDelete = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <StyledBox className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h3" className={classes.title}>
          {t('client.passengerIngestionServerConfiguration')}
        </Typography>
        {!isSuperReaderUser && (
          <Button variant="contained" color="primary" onClick={onCreate}>
            {t('client.createServerConfig')}
          </Button>
        )}
      </Box>
      <ClientServerConfigList
        serverConfigs={serverConfigs}
        onEdit={onEdit}
        onActivate={activateServerConfig}
        onDeactivate={deactivateServerConfig}
        onDelete={onDelete}
      />
      <PassengerIngestionServerConfigDialog
        clientBC={clientBC}
        open={dialogOpen}
        close={handleDialogClose}
        serverConfig={editingServerConfig}
      />
      <ConfirmationDialog
        title={t('client.confirmDeleteServerConfigTitle')}
        message={t('client.confirmDeleteServerConfigMessage')}
        open={confirmDialogOpen}
        positiveAction={confirmDelete}
        negativeAction={cancelDelete}
      />
      <ActivateDialog
        title={activateDialogTitle}
        message={activateDialogMessage}
        open={activateDialogOpen}
        forActivate={!isDeactivate}
        handleOk={activateDialogPostiveFn}
        handleCancel={activateDialogNegativeFn}
      />
    </StyledBox>
  );
}
